import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { User } from 'firebase/auth';
import { FirebaseService } from './services/firebase/firebase.service';
import { environment } from 'src/environments/environment';

export type AuthPipeGenerator = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => any;

// Anonymous users (created for guest access on the room/:id/join page) are
// treated as "not logged in" by route guards, so they're redirected to /login
// when they hit a protected page and never bounced off /login as if they were
// already authenticated.
export const loggedIn: any = (user: User) => !!user && !user.isAnonymous;

const retrieveUser = (firebase: FirebaseService): Promise<User> => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 3000);
    const sub = firebase.auth.onAuthStateChanged((user) => {
      clearTimeout(timeout);
      sub();
      resolve(user);
    }, () => {
      clearTimeout(timeout);
      resolve(null);
    });
  });
};

const ORG_EXEMPT_ROUTES = ['/accept-invite'];

// Refresh from Firebase so a verification done in another tab is picked up,
// then bounce unverified users out — they need to click the email link before
// they can use the app.
const isEmailVerified = async (user: User): Promise<boolean> => {
  if (user.emailVerified) return true;
  try {
    await user.reload();
  } catch {
    return true; // transient — don't lock the user out
  }
  return user.emailVerified;
};

export const angularFireAuthGuard: CanActivateFn = async (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const firebase = inject(FirebaseService);

  try {
    const authPipeFactory = next.data.authGuardPipe as AuthPipeGenerator || (() => loggedIn);
    const user = await retrieveUser(firebase);

    if (user && !user.isAnonymous && !(await isEmailVerified(user))) {
      await firebase.auth.signOut().catch(() => {});
      return router.createUrlTree(['/login'], { queryParams: { reason: 'email-not-verified' } });
    }

    const can = authPipeFactory(next, state)(user);

    if (typeof can === 'boolean' && can && user) {
      // User is authenticated — check org membership unless on an exempt route
      const isExempt = ORG_EXEMPT_ROUTES.some((route) => state.url.startsWith(route));
      if (!isExempt) {
        try {
          const userSnap = await firebase.firestore.docGet(`users/${user.uid}`);
          // A pending local write (e.g. setOnlineStatus on app boot) can mask
          // the server fields in the snapshot view. Skip the redirect while
          // writes are in flight — the listener-driven user.service will fire
          // again once the server confirms and we'll re-check on next nav.
          if (userSnap?.metadata?.hasPendingWrites) {
            return true;
          }
          const userData = userSnap?.data();
          const orgIds: string[] = userData?.organizationIds || [];
          if (!orgIds.length) {
            window.location.href = environment.adminConsoleUrl + '/new';
            return false;
          }
        } catch {
          // If we can't read the user doc, allow through — don't block on transient errors
        }
      }
      return true;
    }

    if (typeof can === 'boolean') {
      return can;
    } else if (Array.isArray(can)) {
      return router.createUrlTree(can);
    } else {
      return router.parseUrl(can);
    }
  } catch {
    return router.createUrlTree(['/login']);
  }
};

export const redirectUnauthorizedTo: (redirect: string|any[]) => any =
  (redirect) => (user: User) => loggedIn(user) || redirect;
export const redirectLoggedInTo: (redirect: string|any[]) => any =
  (redirect) => (user: User) => loggedIn(user) && redirect || true;
